/** "Your daily burn": how the burn is worked out (activity level, watch, or learned), and the BMR. */
import { useEffect, useState, type ReactElement } from 'react';
import { BMR_LABEL, ENERGY_MODE_HELP, ENERGY_MODE_LABEL } from '../../lib/energy';
import { formatDateTime } from '../../lib/dates';
import { errorMessage } from '../../lib/logger';
import { ACTIVITY_LABEL, PAL } from '../../lib/nutrition';
import { ACTIVITY_LEVELS, BMR_MODES, ENERGY_MODES, type ActivityDay, type ActivityLevel, type BmrMode, type EnergyMode } from '../../lib/types';
import { useApp, useAsync, useDayBurn } from '../../state/app';
import { Icon } from '../Icon';
import { ManualActivityModal } from '../ManualActivityModal';
import { Card, Segmented, Spinner } from '../ui';
import { BurnBreakdown } from '../Panels';
import { originName } from '../ActivityPanel';

const fmt = (n: number): string => Math.round(n).toLocaleString();

export function EnergyCard(): ReactElement {
  const { source, profile, setProfile, settings, saveSettings, energy, today, toast, bump, version } = useApp();
  const [mode, setMode] = useState<EnergyMode>(settings.energy_mode);
  const [bmrMode, setBmrMode] = useState<BmrMode>(settings.bmr_mode);
  const [custom, setCustom] = useState<string>(settings.bmr_custom_kcal !== null ? String(settings.bmr_custom_kcal) : '');
  const [includeTef, setIncludeTef] = useState<boolean>(settings.include_tef);
  const [level, setLevel] = useState<ActivityLevel | ''>(profile.activity_level ?? '');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState<boolean>(false);
  const meals = useAsync(() => source.listMeals(today), [source, today, version]);
  const { burn } = useDayBurn(today, meals.data ?? []);

  // Follow changes saved elsewhere (another tab, the phone) while this form is untouched.
  useEffect(() => {
    setMode(settings.energy_mode); setBmrMode(settings.bmr_mode); setIncludeTef(settings.include_tef);
    setCustom(settings.bmr_custom_kcal !== null ? String(settings.bmr_custom_kcal) : '');
  }, [settings.energy_mode, settings.bmr_mode, settings.bmr_custom_kcal, settings.include_tef]);
  useEffect(() => { setLevel(profile.activity_level ?? ''); }, [profile.activity_level]);

  const o = energy.bmrOptions;
  const customKcal: number | null = custom.trim() === '' ? null : Number(custom);
  const dirty: boolean = mode !== settings.energy_mode || bmrMode !== settings.bmr_mode || includeTef !== settings.include_tef
    || (customKcal ?? null) !== settings.bmr_custom_kcal || (level || null) !== profile.activity_level;

  const save = async (): Promise<void> => {
    setError(null);
    if (customKcal !== null && (!Number.isFinite(customKcal) || customKcal < 800 || customKcal > 5000)) { setError('Your own BMR must be a number from 800 to 5,000 kcal.'); return; }
    if (bmrMode === 'custom' && customKcal === null) { setError('Type your BMR, or pick one of the equations.'); return; }
    setSaving(true);
    try {
      await saveSettings({ energy_mode: mode, bmr_mode: bmrMode, bmr_custom_kcal: customKcal === null ? null : Math.round(customKcal), include_tef: includeTef });
      if ((level || null) !== profile.activity_level) setProfile(await source.updateProfile({ activity_level: level === '' ? null : level }));
      toast('Burn settings saved');
      bump();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const bmrRow = (m: BmrMode, value: number | null, missing: string | null): ReactElement => (
    <label key={m} className={`choice ${bmrMode === m ? 'on' : ''}`}>
      <input type="radio" name="bmr" checked={bmrMode === m} onChange={() => setBmrMode(m)} />
      <span className="choice-main">
        <b>{BMR_LABEL[m]}</b>
        {m === 'custom' ? (
          <span className="choice-inline" onClick={(e) => e.preventDefault()}>
            <span className="input-suffix" style={{ width: 150 }}>
              <input className="input" type="number" inputMode="numeric" min={800} max={5000} step={1} placeholder="e.g. 2450" value={custom}
                onChange={(e) => { setCustom(e.target.value); setBmrMode('custom'); }} aria-label="Your BMR" />
              <em>kcal</em>
            </span>
            <small className="faint">From a metabolic test, a DEXA report, or a number you trust.</small>
          </span>
        ) : (
          <small className="faint">{missing ?? (m === 'mifflin' ? 'From weight, height, age and sex. The most accurate general equation (Frankenfield 2005).' : 'From your lean mass. The most accurate standard equation for athletes; weight-based ones run low for muscular people (ten Haaf 2014; Tinsley 2019).')}</small>
        )}
      </span>
      {m !== 'custom' && <span className="choice-val num">{value !== null ? `${fmt(value)} kcal` : '—'}</span>}
    </label>
  );

  const lastRow: ActivityDay | undefined = [...energy.activityRows].reverse().find((r) => r.source === 'health_connect');
  const levelLabel: string = mode === 'activity' ? 'Activity level' : mode === 'wearable' ? 'Activity level (used on days with no watch data)' : 'Activity level (used until your burn is learned)';

  return (
    <Card title="Your daily burn" sub="how many calories you use in a day">
      <div className="stack" style={{ gap: 16 }}>
        <div>
          <Segmented<EnergyMode> options={ENERGY_MODES.map((m) => ({ value: m, label: ENERGY_MODE_LABEL[m] }))} value={mode} onChange={setMode} />
          <p className="muted" style={{ margin: '8px 0 0', fontSize: 13.5 }}>{ENERGY_MODE_HELP[mode]}</p>
        </div>

        <div>
          <div className="field-title">Resting burn (BMR)</div>
          <div className="choices">
            {BMR_MODES.map((m) => bmrRow(m, m === 'mifflin' ? o.mifflin : m === 'lean' ? o.lean : null, m === 'mifflin' ? o.mifflinMissing : m === 'lean' ? o.leanMissing : null))}
          </div>
          {energy.bmr?.wanted && <div className="note warn" style={{ marginTop: 8 }}>{BMR_LABEL[energy.bmr.wanted]} can’t run yet, so {BMR_LABEL[energy.bmr.method]} is used ({fmt(energy.bmr.kcal)} kcal).</div>}
        </div>

        <label className="field"><span>{levelLabel}</span>
          <select className="input" value={level} onChange={(e) => setLevel(e.target.value as ActivityLevel | '')}>
            <option value="">— (counts as sedentary)</option>
            {ACTIVITY_LEVELS.map((a) => <option key={a} value={a}>{ACTIVITY_LABEL[a]} · ×{PAL[a]}</option>)}
          </select>
        </label>

        {mode === 'wearable' && (
          <div className="stack" style={{ gap: 10 }}>
            <label className="check"><input type="checkbox" checked={includeTef} onChange={(e) => setIncludeTef(e.target.checked)} />
              Add the energy spent digesting food (about 10% of what you eat)</label>
            <div className="note info">
              <div className="row" style={{ gap: 8 }}><Icon name="watch" size={16} /><b>Watch data</b></div>
              {lastRow ? (
                <div style={{ marginTop: 4 }}>
                  Last sent by your phone{lastRow.synced_at ? ` ${formatDateTime(lastRow.synced_at)}` : ''}
                  {lastRow.origins.length > 0 && <> from {lastRow.origins.map(originName).join(', ')}</>}.
                </div>
              ) : (
                <div style={{ marginTop: 4 }}>No watch data yet. Open Mighty Peanut Diet on your phone, go to More, and connect Health Connect. Or type your watch’s numbers in by hand.</div>
              )}
              <button type="button" className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => setManual(true)}><Icon name="edit" size={15} /> Enter today’s watch numbers</button>
            </div>
          </div>
        )}

        <div className="burn-summary">
          <div className="burn-line"><span>Today</span><BurnBreakdown burn={burn} /></div>
          <div className="burn-line">
            <span>Typical day</span>
            <span>{energy.planning.kcal !== null ? <><b className="num">{fmt(energy.planning.kcal)} kcal</b> {energy.planning.method === 'wearable' ? `(average of ${energy.planning.days} watch days)` : energy.planning.method === 'observed' ? '(learned from your weight trend)' : energy.planning.method === 'activity' ? '(BMR × activity level)' : ''}</> : '—'}</span>
          </div>
          {energy.planning.note && <div className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>{energy.planning.note}</div>}
        </div>

        {error !== null && <div className="note err">{error}</div>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving || !dirty}>{saving ? <Spinner /> : <Icon name="check" size={18} />} Save burn settings</button>
        </div>
      </div>
      {manual && <ManualActivityModal day={today} onClose={() => setManual(false)} />}
    </Card>
  );
}
